import { useHistory } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
    Grid,
    Box,
    IconButton,
    Dialog,
    DialogContent,
    Stack,
    Typography,
} from '@mui/material'
import Logo from 'assets/logo.png'
import ROUTE from 'constant/route'
import EnterLoginText from 'views/Login/EnterLoginText'
import 'swiper/css'
import useAuth from 'hooks/useAuth/useAuth'
import LoginBottom from 'views/Login/LoginBottom'
import WifiStatus from 'components/WifiStatus/WifiStatus'
import LanguageSwitcher from 'components/LanguageSwitcher/LanguageSwitcher'
import SwitchLogin from './SwitchLogin'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import { isUsingNonClientTokenType } from 'utilities/jwtHelper'
import LoginProvider, { useLoginContext } from './LoginProvider'
import OverrideLoginPopup from 'views/Login/OverrideLogginPopup/OverrideLoginPopup'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import receiptline from 'receiptline'

const SLIDE_SPEED = 120

function CurrentUrlQRCodeDialog({
    open,
    onClose,
}: {
    open: boolean
    onClose: () => void
}) {
    const [qrImage, url] = useMemo(() => {
        try {
            const parsedUrl = new URL(window.location.href)
            const hostNameWithSchema =
                parsedUrl.protocol + '//' + parsedUrl.host
            const svg = receiptline.transform(
                `{code:${hostNameWithSchema}; option:qrcode,8,L}`,
                { command: 'svg' },
            )
            const base64 = btoa(unescape(encodeURIComponent(svg)))
            return [`data:image/svg+xml;base64,${base64}`, hostNameWithSchema]
        } catch (e) {
            return ['', '']
        }
    }, [])

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogContent>
                <Stack spacing={2} alignItems="center">
                    <Typography variant="h5">Current URL: {url}</Typography>
                    {qrImage && (
                        <img
                            width={'100%'}
                            src={qrImage}
                            alt="qr-current-url"
                        />
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

function LoginPageContainer() {
    const { isMobile, isLandscapeMobile, isMobileTablet } = useScreenSize()
    const { isDoingLogin } = useLoginContext()
    const [isLoginWithEmail, setIsLoginWithEmail] = useState(false)
    const [qrDialog, setQrDialog] = useState(false)
    const { isAuthenticated } = useAuth()

    const swiperRef = useRef<any>(null)

    const history = useHistory()

    useEffect(() => {
        if (isAuthenticated) {
            // check token type
            const result = isUsingNonClientTokenType()
            // Extremely case. result can be undefined in-case Cookies was suddenly removed with some reasons e.g. from other tabs
            // In this case just keep showing the login page
            if (result === undefined) return

            if (result) history.replace(ROUTE.SELECT_DEVICE)
            else history.replace(ROUTE.MAIN)
        }
    }, [history, isAuthenticated])

    return (
        <>
            <Grid
                container
                sx={{
                    height: '100vh',
                    overflow: 'auto',
                    background:
                        'radial-gradient(circle, rgba(90, 223, 180, 1) 0%, rgba(18, 174, 133, 1) 50%, rgba(16, 172, 132, 1) 100%)',
                }}
            >
                <Grid
                    item
                    xs={12}
                    container
                    justifyContent="space-between"
                    alignItems="flex-start"
                    position={isMobile ? 'relative' : 'absolute'}
                >
                    <Grid item>
                        <Box p={0.5}>
                            <LanguageSwitcher />
                        </Box>
                    </Grid>
                    <Grid item>
                        <Box display="flex" alignItems="center">
                            <WifiStatus />
                            <IconButton
                                sx={{ mt: 1, color: '#fff' }}
                                onClick={() => setQrDialog(true)}
                                size="large"
                            >
                                <QrCode2Icon />
                            </IconButton>
                        </Box>
                    </Grid>
                </Grid>
                <Grid
                    item
                    xs={12}
                    container
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                        width: '100%',
                        flexWrap: 'nowrap',
                        pt: isMobile
                            ? 0
                            : isMobileTablet
                            ? isLandscapeMobile
                                ? 2
                                : 4
                            : 8,
                        pb: 2,
                    }}
                >
                    <Grid
                        item
                        container
                        zIndex={1}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Grid item>
                            <Box
                                component="img"
                                src={Logo}
                                alt="Logo"
                                sx={{
                                    width: isLandscapeMobile
                                        ? '150px'
                                        : isMobile
                                        ? '250px'
                                        : '100%',
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid item>
                        {!isDoingLogin && (
                            <EnterLoginText
                                isLoginWIthEmail={isLoginWithEmail}
                            />
                        )}
                    </Grid>
                    <Grid
                        item
                        sx={{
                            width: '100% !important',
                        }}
                    >
                        <SwitchLogin
                            loading={isDoingLogin}
                            ref={swiperRef}
                            isLoginWithEmail={isLoginWithEmail}
                            setIsLoginWithEmail={setIsLoginWithEmail}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12} container alignItems={'flex-end'}>
                    <Grid item xs={12}>
                        <LoginBottom
                            hideIdLogin={isLoginWithEmail}
                            onClick={() => {
                                if (swiperRef && swiperRef.current)
                                    swiperRef.current.slideNext(SLIDE_SPEED)
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <CurrentUrlQRCodeDialog
                open={qrDialog}
                onClose={() => setQrDialog(false)}
            />
        </>
    )
}

function LoginPage() {
    return (
        <LoginProvider>
            <LoginPageContainer />
            <OverrideLoginPopup />
        </LoginProvider>
    )
}

export default LoginPage
